/**
 * useRecordVisit hook
 *
 * Hook that automatically records a visit when an entity ID is available.
 * Handles edge cases like undefined ID and SSR.
 * Uses ref to prevent duplicate tracking on re-renders.
 */

import { recordVisit } from 'lib/utils/recent-items-tracker'
import { useEffect, useRef } from 'react'

/**
 * Hook to automatically record a visit to an item
 *
 * @param category - The category to record the visit for (e.g., 'apps', 'groups')
 * @param id - The entity ID to record a visit for (undefined/null will be ignored)
 *
 * @example
 * // In app details page
 * const { app_id: appId } = router.query
 * useRecordVisit(RecentItemsCategory.APPS, typeof appId === 'string' ? appId : undefined)
 */
export function useRecordVisit(category: string, id: string | undefined | null): void {
    // Track which ID we've already recorded a visit for in this component instance
    const recordedIdRef = useRef<string | null>(null)

    useEffect(() => {
        // Skip if no ID provided
        if (!id) {
            return
        }

        // Skip if we've already recorded this ID in this component instance
        // This prevents duplicate tracking on re-renders
        if (recordedIdRef.current === id) {
            return
        }

        // Record the visit
        recordVisit(category, id)
        recordedIdRef.current = id
    }, [category, id])
}
